<template>
    <section class="mt-10">
        <header class="flex flex-wrap items-end justify-between gap-4">
            <div>
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
                    Sellers que aderiram
                </h3>
                <p
                    class="mt-0.5 max-w-xl text-xs text-gray-500 dark:text-gray-400"
                >
                    Pelo dashboard, o próprio seller aceita o termo. Pelo admin,
                    alguém liga em Usuários &gt; Configurações.
                </p>
            </div>
            <form
                class="relative w-full sm:w-72"
                role="search"
                @submit.prevent="fetch(1)"
            >
                <svg
                    class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fill-rule="evenodd"
                        d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                        clip-rule="evenodd"
                    />
                </svg>
                <input
                    v-model="search"
                    type="text"
                    placeholder="ID ou e-mail do seller"
                    aria-label="Buscar sellers"
                    class="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:ring-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white dark:focus:ring-white"
                    @input="debouncedFetch"
                />
            </form>
        </header>

        <div
            class="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 dark:border-gray-700 dark:bg-gray-700 lg:grid-cols-4"
        >
            <button
                v-for="tile in tiles"
                :key="tile.value"
                type="button"
                class="group relative bg-white px-4 pb-3 pt-4 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-900 dark:bg-gray-800 dark:hover:bg-gray-800/60 dark:focus-visible:ring-white"
                :aria-pressed="origin === tile.value"
                @click="selectOrigin(tile.value)"
            >
                <span
                    class="absolute inset-x-0 top-0 h-0.5 transition-opacity"
                    :class="[
                        tile.dot,
                        origin === tile.value
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-40',
                    ]"
                ></span>
                <span
                    class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
                >
                    <span
                        class="h-1.5 w-1.5 rounded-full"
                        :class="tile.dot"
                    ></span>
                    {{ tile.label }}
                </span>
                <span
                    class="mt-1 block text-2xl font-semibold tabular-nums tracking-tight text-gray-900 dark:text-white"
                >
                    {{ number(count(tile.value)) }}
                </span>
            </button>
        </div>

        <div
            class="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
        >
            <div class="overflow-x-auto">
                <table class="min-w-full text-left text-sm">
                    <thead
                        class="bg-gray-50/80 text-[11px] uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400"
                    >
                        <tr>
                            <th class="py-2.5 pl-5 pr-4 font-medium">Seller</th>
                            <th class="px-4 py-2.5 font-medium">Adesão</th>
                            <th class="px-4 py-2.5 text-right font-medium">
                                Taxa
                            </th>
                            <th class="px-4 py-2.5 text-right font-medium">
                                Envio
                            </th>
                            <th class="py-2.5 pl-4 pr-5">
                                <span class="sr-only">Ações</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody
                        v-if="loading"
                        class="divide-y divide-gray-100 dark:divide-gray-700/50"
                        aria-busy="true"
                    >
                        <tr v-for="row in 4" :key="row">
                            <td class="py-4 pl-5 pr-4">
                                <div
                                    class="h-3 w-32 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                ></div>
                                <div
                                    class="mt-2 h-2.5 w-40 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                ></div>
                            </td>
                            <td class="px-4 py-4">
                                <div
                                    class="h-3 w-40 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                ></div>
                            </td>
                            <td class="px-4 py-4">
                                <div
                                    class="ml-auto h-3 w-12 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                ></div>
                            </td>
                            <td class="px-4 py-4">
                                <div
                                    class="ml-auto h-3 w-12 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
                                ></div>
                            </td>
                            <td class="py-4 pl-4 pr-5"></td>
                        </tr>
                    </tbody>

                    <tbody v-else-if="!adhesions.length">
                        <tr>
                            <td colspan="5" class="px-5 py-14 text-center">
                                <p
                                    class="text-sm text-gray-900 dark:text-white"
                                >
                                    {{
                                        search || origin
                                            ? "Nenhum seller com esse filtro."
                                            : "Nenhum seller aderiu ainda."
                                    }}
                                </p>
                            </td>
                        </tr>
                    </tbody>

                    <tbody
                        v-else
                        class="divide-y divide-gray-100 dark:divide-gray-700/50"
                    >
                        <tr
                            v-for="adhesion in adhesions"
                            :key="adhesion.user_id"
                            class="align-top text-gray-900 transition-colors hover:bg-gray-50/70 dark:text-white dark:hover:bg-gray-900/30"
                        >
                            <td class="relative py-3.5 pl-5 pr-4">
                                <span
                                    class="absolute inset-y-0 left-0 w-0.5"
                                    :class="rail(adhesion)"
                                    aria-hidden="true"
                                ></span>
                                <router-link
                                    :to="`/user/${adhesion.user_id}`"
                                    class="text-sm hover:underline hover:underline-offset-2"
                                >
                                    {{ adhesion.name }}
                                </router-link>
                                <p
                                    class="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
                                >
                                    #{{ adhesion.user_id }} ·
                                    {{ adhesion.email }}
                                </p>
                            </td>
                            <td class="px-4 py-3.5 text-xs">
                                <p
                                    class="flex items-center gap-1.5 whitespace-nowrap"
                                >
                                    <span
                                        class="h-2 w-2 rounded-full"
                                        :class="rail(adhesion)"
                                    ></span>
                                    <span
                                        class="text-gray-700 dark:text-gray-200"
                                        >{{ originLabel(adhesion) }}</span
                                    >
                                    <span
                                        class="tabular-nums text-gray-400"
                                        :title="fullDate(adhesion.enabled_at)"
                                        >{{
                                            shortDate(adhesion.enabled_at)
                                        }}</span
                                    >
                                </p>
                                <p
                                    v-if="
                                        adhesion.origin === 'admin' &&
                                        adhesion.enabled_by_name
                                    "
                                    class="mt-0.5 text-gray-500 dark:text-gray-400"
                                >
                                    por {{ adhesion.enabled_by_name }}
                                </p>
                            </td>
                            <td
                                class="px-4 py-3.5 text-right text-xs tabular-nums text-gray-700 dark:text-gray-300"
                            >
                                {{ money(adhesion.price ?? defaultPrice) }}
                                <p
                                    v-if="adhesion.price === null"
                                    class="mt-0.5 text-gray-400"
                                >
                                    padrão
                                </p>
                            </td>
                            <td
                                class="px-4 py-3.5 text-right text-xs tabular-nums text-gray-700 dark:text-gray-300"
                            >
                                <template
                                    v-if="adhesion.release_delay_days === null"
                                    ><span class="text-gray-400"
                                        >padrão</span
                                    ></template
                                >
                                <template v-else
                                    >{{ adhesion.release_delay_days }}
                                    {{
                                        adhesion.release_delay_days === 1
                                            ? "dia"
                                            : "dias"
                                    }}</template
                                >
                            </td>
                            <td class="py-3.5 pl-4 pr-5 text-right">
                                <button
                                    v-if="adhesion.enabled"
                                    type="button"
                                    class="whitespace-nowrap rounded-md border border-red-200 px-2.5 py-1 text-xs text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-900/20"
                                    :disabled="disabling === adhesion.user_id"
                                    @click="disable(adhesion)"
                                >
                                    {{
                                        disabling === adhesion.user_id
                                            ? "Desativando..."
                                            : "Desativar"
                                    }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <footer
                v-if="pagination && pagination.total"
                class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-2.5 text-xs text-gray-500 dark:border-gray-700/50 dark:text-gray-400"
            >
                <span class="tabular-nums"
                    >{{ pagination.from }} a {{ pagination.to }} de
                    {{ number(pagination.total) }}</span
                >
                <Pagination
                    v-if="pagination.last_page > 1"
                    :pagination="pagination"
                    @paginate="fetch"
                />
            </footer>
        </div>
    </section>
</template>

<script>
import { useToast } from "vue-toastification";
import Pagination from "../layouts/Pagination.vue";

const SEARCH_DELAY_MS = 350;

export default {
    components: { Pagination },
    setup() {
        return { toast: useToast() };
    },
    data() {
        return {
            loading: false,
            adhesions: [],
            pagination: null,
            counts: {},
            defaultPrice: 0,
            origin: "",
            search: "",
            searchTimer: null,
            requestId: 0,
            disabling: null,
        };
    },
    computed: {
        tiles() {
            return [
                { value: "", label: "Todos", dot: "bg-gray-900 dark:bg-white" },
                {
                    value: "dashboard",
                    label: "Pelo dashboard",
                    dot: "bg-green-500",
                },
                { value: "admin", label: "Pelo admin", dot: "bg-blue-500" },
                { value: "disabled", label: "Desligados", dot: "bg-gray-400" },
            ];
        },
    },
    mounted() {
        this.fetch();
    },
    methods: {
        count(origin) {
            if (origin) {
                return this.counts[origin] ?? 0;
            }

            return Object.values(this.counts).reduce(
                (sum, value) => sum + value,
                0,
            );
        },
        rail(adhesion) {
            if (!adhesion.enabled) {
                return "bg-gray-400";
            }

            return adhesion.origin === "dashboard"
                ? "bg-green-500"
                : "bg-blue-500";
        },
        originLabel(adhesion) {
            if (!adhesion.enabled) {
                return "Desligado";
            }

            return adhesion.origin === "dashboard"
                ? "Pelo dashboard"
                : "Pelo admin";
        },
        number(value) {
            return Number(value).toLocaleString("pt-BR");
        },
        money(cents) {
            return (cents / 100).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
            });
        },
        fullDate(value) {
            return value
                ? new Date(value).toLocaleString("pt-BR", {
                      dateStyle: "short",
                      timeStyle: "medium",
                  })
                : "";
        },
        shortDate(value) {
            return value
                ? new Date(value).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "2-digit",
                  })
                : "";
        },
        selectOrigin(origin) {
            this.origin = this.origin === origin ? "" : origin;
            this.fetch(1);
        },
        debouncedFetch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.fetch(1), SEARCH_DELAY_MS);
        },
        disable(adhesion) {
            const confirmed = window.confirm(
                `Desativar o Vale Compras de ${adhesion.name} (#${adhesion.user_id})? As próximas vendas não geram cashback nem taxa.`,
            );

            if (!confirmed) {
                return;
            }

            this.disabling = adhesion.user_id;
            axios
                .put(
                    `/api/services/crm-bonus/adhesions/${adhesion.user_id}/disable`,
                )
                .then(() => {
                    this.toast.success("Vale Compras desativado.");
                    this.fetch(this.pagination?.current_page ?? 1);
                })
                .catch((error) =>
                    this.toast.error(
                        error.response?.status === 403
                            ? "Você não tem permissão para desativar o Vale Compras."
                            : "Não foi possível desativar o Vale Compras.",
                    ),
                )
                .finally(() => {
                    this.disabling = null;
                });
        },
        fetch(page = 1) {
            clearTimeout(this.searchTimer);
            // Uma busca lenta nao sobrescreve a resposta de uma busca mais nova.
            const requestId = ++this.requestId;
            this.loading = true;
            axios
                .get("/api/services/crm-bonus/adhesions", {
                    params: {
                        page,
                        origin: this.origin || undefined,
                        search: this.search.trim() || undefined,
                    },
                })
                .then(({ data }) => {
                    if (requestId !== this.requestId) {
                        return;
                    }

                    this.counts = data.counts;
                    this.defaultPrice = data.default_price;
                    this.adhesions = data.data;
                    this.pagination = data;
                })
                .catch(() =>
                    this.toast.error(
                        "Não foi possível carregar os sellers do CRM Bonus.",
                    ),
                )
                .finally(() => {
                    if (requestId === this.requestId) {
                        this.loading = false;
                    }
                });
        },
    },
};
</script>
